import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox } from '../../../shared/components/checkbox';
import { FormLabel } from '../../../shared/components/form-label';
import { NumericStepper } from '../../../shared/components/numeric-stepper';
import { Select } from '../../../shared/components/select';
import { ValidationMessage } from '../../../shared/components/validation-message';
import { useSettings } from '../../../shared/hooks/use-settings';
import { LOAD_TYPES_QUERY, type LoadTypesQueryResult } from '../graphql';
import type { WizardFormValues } from '../schemas';
import { StepHeading } from './step-heading';

export const ALLOWED_LOAD_TYPES = new Set(['Storage', 'Office']);

export function Step2Purpose() {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<WizardFormValues>();
  const { settings } = useSettings();
  const { data } = useQuery<LoadTypesQueryResult>(LOAD_TYPES_QUERY);
  const loadTypeValue = useWatch({ control, name: 'loadType' });

  const loadTypes = useMemo(
    () =>
      (data?.loadTypeDefaults ?? [])
        .filter((lt) => ALLOWED_LOAD_TYPES.has(lt.description))
        .sort((a, b) => a.description.localeCompare(b.description)),
    [data],
  );

  const selectedLoadType = useMemo(() => {
    if (loadTypeValue == null || loadTypeValue === '') return null;
    return loadTypes.find((lt) => lt.id === Number(loadTypeValue)) ?? null;
  }, [loadTypeValue, loadTypes]);

  const loadTypeDescription =
    settings && selectedLoadType
      ? (settings.get(`loadTypeDescription${selectedLoadType.description}`) as string)
      : '';

  return (
    <div className="space-y-5">
      <StepHeading
        title="What's the floor for?"
        description="The load type and height affect the structural design and the price."
      />

      <div>
        <FormLabel htmlFor="loadType">Load type</FormLabel>
        <Select id="loadType" {...register('loadType')}>
          <option value="">-- Please select a load type --</option>
          {loadTypes.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.description}
            </option>
          ))}
        </Select>
        <ValidationMessage
          error={errors.loadType as never}
          message={errors.loadType?.message ?? ''}
        />
        {loadTypeDescription && (
          <p className="text-sm text-hilevel-gray mt-2">{loadTypeDescription}</p>
        )}
      </div>

      <div>
        <FormLabel htmlFor="height">Height</FormLabel>
        <Controller
          control={control}
          name="height"
          render={({ field }) => (
            <NumericStepper
              id="height"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              min={1}
              max={10}
              suffixLabel="m"
            />
          )}
        />
        <ValidationMessage error={errors.height as never} message={errors.height?.message ?? ''} />
      </div>

      <Checkbox
        id="fireProtection"
        label="Include fire protection"
        {...register('fireProtection')}
      />
    </div>
  );
}
